import { closestCenter, DndContext, type DragEndEvent } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useQueryClient } from '@tanstack/react-query';
import { L } from 'harmony-language';
import React from 'react';
import { OrgQueryKeys } from '../../../../api/config';
import { useAddTravelTimes } from '../../../../api/mutations/reworked/add/use-add-travel-times';
import { useUser } from '../../../../api/queries/use-user';
import type { Stop } from '../../../../types';
import { stopsToOrganizationLocationIdPairs } from '../../../../utils/data-mapping';
import { findUnknownPairsInCache, TravelTimesCache, updatePointlessPairs } from '../../../travel-times/use-travel-times';
import { PerformanceDragIcon } from '../../drafts-loads-table-cells/performance-icons';
import { DisplayStopLocation } from '../../drafts-loads-table-cells/stops/display/display-stop-location';
import { DisplayStopText } from '../../drafts-loads-table-cells/stops/display/display-stop-text';
import { DisplayStopTime } from '../../drafts-loads-table-cells/stops/display/display-stop-time';
import { DisplayStopType } from '../../drafts-loads-table-cells/stops/display/display-stop-type';

const stopGridColumnsStyle = {
    gridTemplateColumns: '0.2fr 0.6fr 1fr 1fr 1fr'
}

interface BulkScheduleStopsProps {
    draftStops: Stop[];
    onUpdateStops: (s: Stop[]) => void;
}

export const BulkScheduleStops = (props: BulkScheduleStopsProps) => {
    const { draftStops, onUpdateStops } = props;
    
    const { organizationId } = useUser();
    const { mutate: addTravelTimes } = useAddTravelTimes(organizationId);
    const queryKey = OrgQueryKeys.resolve(organizationId, OrgQueryKeys.locationTravelTimes);
    const queryClient = useQueryClient();
    const travelTimesCache = queryClient.getQueryData<TravelTimesCache>(([queryKey]));

    const handleOnDragEnd = (event: DragEndEvent) => {
        const reorderedStops = [...draftStops];
        if (!event.over || typeof event.active.id === 'string' || typeof event.over.id === 'string') {
            return reorderedStops;
        }

        const dragIndex = event.active.id - 1;
        const hoverIndex = event.over.id - 1;
        const dragStop = draftStops[dragIndex];

        const updatedStops = reorderedStops.resortValue(dragIndex, hoverIndex, dragStop).map((stop, i) => ({
            ...stop,
            sequence: i + 1,
        }));

        onUpdateStops(updatedStops);

        // This section will update travel times across stops after they're reordered
        if (travelTimesCache) {
            const pairs = stopsToOrganizationLocationIdPairs(updatedStops);
            const unknowPairs = findUnknownPairsInCache(travelTimesCache, pairs);
            const filteredUnknowPairs = updatePointlessPairs(unknowPairs, travelTimesCache, queryClient, queryKey);

            if (filteredUnknowPairs.length) {
                addTravelTimes(unknowPairs);
            }
        }
    };

    return (
        <div id='stopsContainer' style={{ marginLeft: '1rem' }}>
            <div
                className='lt-row-stop lt-row-stop-header'
                style={stopGridColumnsStyle}
            >
                <div/>
                <div>{L.stopType()}</div>
                <div>{L.location()}</div>
                <div>{L.scheduled()}</div>
                <div>{L.productionPlan()}</div>
            </div>

            <DndContext
                onDragEnd={handleOnDragEnd}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                collisionDetection={closestCenter}
            >
                <SortableContext
                    items={draftStops.map(x => x.sequence)}
                    strategy={verticalListSortingStrategy}
                >
                    {draftStops.map((stop) =>
                        <BulkScheduleStopRow
                            key={stop.id}
                            stop={stop}
                        />
                    )}
                </SortableContext>
            </DndContext>
        </div>
    );
};

const BulkScheduleStopRow = (props: { stop: Stop }) => {
    const { stop } = props;
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: stop.sequence });

    const dragStyle = {
        transform: CSS.Translate.toString(transform),
        transition: transition,
    };

    return (
        <div
            className='lt-row-stop lt-row-stop-item'
            ref={setNodeRef}
            style={{ display: 'grid', ...dragStyle, ...stopGridColumnsStyle }}
        >
            <PerformanceDragIcon
                isDragging={isDragging}
                listeners={listeners}
                attributes={attributes}
                isCarrier={false}
            />
            <div className='lt-cell lt-cell-sub'><DisplayStopType stop={stop} /></div>
            <div className='lt-cell lt-cell-sub'><DisplayStopLocation stop={stop} /></div>
            <div className='lt-cell lt-cell-sub'><DisplayStopTime dateTime={stop.arrivalTime} /></div>
            <div className='lt-cell lt-cell-sub'><DisplayStopText text={stop.productionPlan} /></div>
        </div>
    );
}
